/**
 * 环境
 */
import { getSystem } from '@mx/util/get-system';
import { getAllCookie } from '@mx/util/get-all-cookie';
import { getAllQueryString } from '@mx/util/get-all-query-string';
import { getEnv } from '../neo';
import { bridge } from './bridge';
import { PageStyle, Runtime, SceneType } from './enum-type';
import { getAppName } from './utils';

const UA = window.navigator.userAgent.toLocaleLowerCase();

export interface QueryParamsType {
  zone_user_id: string;
  member_id: string;
}
class Env {
  system: string;

  isIOS: boolean;

  isAndroid: boolean;

  isHarmony: boolean;

  isTitans: boolean;

  isKNB: boolean;

  isInDianping: boolean;

  isMeituan: boolean;

  appName: string;

  runtime: Runtime;

  // common params
  entry: string; // 区分不同入口

  utmSource: string; // 鲸湾侧配置营销

  pageStyle: string; // 区分页面展示类型全页面(page)和弹窗(pop)两种类型

  openPageStyle: string; // 区分极速支付非支付场景开通页面的样式

  signStatus: number;

  commonParams: {
    nb_app: string;
    nb_uuid: string;
    nb_appversion: string;
    nb_platform: string;
    nb_fingerprint?: string;
    nb_location?: string;
    paySDKVersion?: string;
    userid?: string;
    nb_version?: string;
  };

  cashierParams: Partial<CashierParams> = {};

  // 业务参数
  mtPlanId: string;

  imageUrl: string;

  // 日志查询
  payRequestId: string;

  traceId: string = '';

  /** 隐藏月付、绑卡等拉新入口 */
  hideEntry: boolean = false;

  // Hybrid 极速支付追加参数
  network: string;

  isOffline: boolean;

  nbVersion: string = '';

  nbContainer: string = 'hybrid';

  webAppVersion: string = '';

  neoStatTime: string[] = [];

  hybridCurrentUrl: string = window.location.href.split('?')[0].split('#')[0];

  requestQueryParams: QueryParamsType = { zone_user_id: '', member_id: '' };

  uniqueId: string;

  isMock: boolean = false;

  hybridVersion: string = '';

  memberId: string;

  /** url 参数 ehc_transparent_page=1 为接入 ehc 透明容器标识 */
  isEhcTransparentPage: boolean = false;

  /** 场景值，用于区分不同页面走不同的逻辑 */
  sceneType: SceneType = SceneType.oneClickModal;

  constructor() {
    const cookies = getAllCookie();
    const urlParams = getAllQueryString();

    const getParam = (...names: string[]) => {
      for (let i = 0, len = names.length; i < len; i++) {
        const name = names[i];
        const param = cookies[name] || urlParams[name];
        if (param) {
          return param;
        }
      }
      return '';
    };

    if (/\.dev\.sankuai\.com/.test(window.location.host)) {
      this.runtime = Runtime.Dev;
    } else if (/\.test\.sankuai\.com/.test(window.location.host)) {
      this.runtime = Runtime.Test;
    } else if (/\.st\.(sankuai|meituan)\.com/.test(window.location.host)) {
      this.runtime = Runtime.Staging;
    } else if (/\.meituan\.com/.test(window.location.host)) {
      this.runtime = Runtime.Prod;
    } else {
      this.runtime = Runtime.Local;
    }

    // url params
    this.entry = urlParams.entry || '';
    this.utmSource = urlParams.utm_source || '';
    this.pageStyle = urlParams.pageStyle || PageStyle.FullPage;
    this.hideEntry = urlParams.hideEntry === '1' || false;
    this.signStatus = Number(urlParams.signStatus || 1);
    this.mtPlanId = urlParams.mtPlanId || '0';
    // 示例图片 https://p0.meituan.net/scarlett/da0336b6cf3beea7b2ae8b2117dfeb5c846167.gif
    this.imageUrl = urlParams.image_url || '';
    this.openPageStyle = urlParams.openPageStyle || 'full'; // 兜底到新版全页极速支付开启页面，全量新版极速支付开启全页页面。
    this.memberId = urlParams.member_id || '';
    this.isEhcTransparentPage = urlParams.ehc_transparent_page === '1' || false;

    // app
    this.appName = getAppName(urlParams.utm_campaign, urlParams.nb_app);
    this.system = getSystem();
    this.isIOS = this.system === 'ios';
    this.isAndroid = this.system === 'android';
    this.isHarmony = /openharmony/.test(UA);
    this.isTitans = (KNB && KNB.env && KNB.env.isTitans) || false;
    this.isKNB =
      Boolean(KNB?.env?.isTitans) || Boolean(KNB?.env?.isStandardKnb) || false;
    this.isInDianping = this.appName === 'dianping-nova';
    this.isMeituan = this.appName === 'group';

    this.commonParams = {
      nb_app: this.appName,
      nb_uuid: getParam('iuuid', 'uuid', 'dpid'),
      nb_appversion: getParam('version_name', '_utm_term', 'utm_term'),
      nb_platform: this.getPlatfrom(),
      userid: getParam('u', 'userid', 'userId'),
    };

    this.payRequestId = getParam('payRequestId');

    this.setLocation();
    this.setFingerprint();
    this.getPaySDKVersion();

    // Hybrid 极速支付追加参数
    const neoEnv = getEnv();
    this.network = neoEnv.network;
    this.isOffline = neoEnv.isOffline;
    this.uniqueId = neoEnv.neoUniqueId;
    // iOS 9 以下系统，带括号正则分组解析可能会抛出异常
    try {
      // eslint-disable-next-line no-useless-escape
      // const result = /\/(\d+\.\d+\.\d+)\//g.exec(window.location.pathname);
      // if (result && result.length === 2) {
      //   this.hybridVersion = result[1];
      // }
      // 抹平版本后需要每次手动更新版本，方便统计整体的覆盖率
      this.hybridVersion = '0.7.2';
    } catch (error) {
      console.warn(error);
    }
  }

  /**
   * 设置 Neo 统计时间
   * @param neoStatTime Neo 统计时间
   */
  setNeoStatTime(neoStatTime: any[]) {
    this.neoStatTime = neoStatTime;
  }

  /**
   * 设置请求 query 参数（SET 化参数）
   * @param queryParams 请求 query 参数（SET 化参数）
   */
  setRequestQueryParams(requestQueryParams: QueryParamsType): void {
    this.requestQueryParams = requestQueryParams;
  }

  /**
   * 设置支付 SDK 版本
   * @param nbVersion 支付 SDK 版本
   */
  setNbVersion(nbVersion: string) {
    this.nbVersion = nbVersion;
  }

  setIsMock(isMock: boolean) {
    this.isMock = isMock;
  }

  private getPlatfrom() {
    let platform = 'touch';

    if (this.isKNB) {
      const matches = /android|iphone|ipad|ipod|harmony/g.exec(UA);

      platform = matches?.length ? matches[0] : 'touch';
      if (platform === 'ipad' || platform === 'ipod') {
        platform = 'iphone';
      }
    }
    return platform as 'iphone' | 'android' | 'harmony' | 'touch';
  }

  private setLocation() {
    bridge
      ?.getLocation()
      .then((location: any) => {
        this.commonParams.nb_location = `${location.lat}_${location.lng}`;
      })
      .catch((error) => {
        console.log('env.log::', error);
      });
  }

  private setFingerprint() {
    bridge?.getFingerprint().then((fingerprint: any) => {
      this.commonParams.nb_fingerprint = fingerprint;
    });
  }

  private getPaySDKVersion() {
    bridge?.getPaymentSDKVersion().then((version: any) => {
      this.commonParams.paySDKVersion = version;
      this.commonParams.nb_version = version;
    });
  }

  // 获取 set 化参数
  initParamsOfSet(): Promise<any> {
    if (!this.isKNB || this.memberId) {
      return Promise.resolve();
    }
    let isSettled = false;
    return new Promise((resolve, reject) => {
      KNB.ready(() => {
        KNB.use('pay.getPayCommonParam', {
          success: (res: any) => {
            isSettled = true;
            if (!res) {
              resolve('');
              return;
            }
            this.memberId = res.member_id;
            resolve('');
          },
          fail: (error: any) => {
            if (isSettled) {
              return;
            }
            isSettled = true;
            // env 不能调用 monitor，会造成循环引用报错
            window.Owl.addError(
              { name: 'set 化参数获取失败', msg: error.message },
              { tags: error, level: 'info', category: 'custom_info' }
            );
            reject();
          },
        });
      });
      // 超时处理
      setTimeout(() => {
        if (isSettled) {
          return;
        }
        isSettled = true;
        window.Owl.addError('获取set 化参数超时', {
          level: 'info',
          category: 'custom_info',
          tags: {
            knbReady: typeof KNB.ready === 'function',
            memberId: this.memberId,
          },
        });
        reject();
      }, 50);
    });
  }

  setSceneType(sceneType: SceneType) {
    this.sceneType = sceneType;
  }

  setCashierParams(cashierParams: Partial<CashierParams>) {
    const nbParams = Object.keys(cashierParams).reduce((acc, key) => {
      if (/^nb_/.test(key)) {
        acc[key] = cashierParams[key as keyof CashierParams];
      }
      return acc;
    }, {} as Record<string, any>);

    this.cashierParams = {
      ...nbParams,
      userId: cashierParams?.userId ?? '',
    };
  }
}

export const env = new Env();
